import { HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { BitsApis } from "@app/core/APIs/bits-apis";
import { CrudApiOptions, CrudApiService, GenResp } from "@ez-library/services/crud-api.service";
import { Observable, catchError, map, of, throwError } from "rxjs";
import {
    OperationsCaseCloseoutDto,
    OperationsCaseCloseoutRequest,
    OperationsCaseCompletionDto,
    OperationsCaseCompletionRequest,
} from "./phms-case-lifecycle.models";

const requestHeaders = new HttpHeaders({
    "Content-Type": "application/json",
    Accept: "application/json, text/plain, */*",
    "skip-error-interceptor": "yes",
    "skip-loading-interceptor": "yes",
});

const lifecycleApiOptions: Partial<CrudApiOptions<"body", "json">> = {
    retries: 0,
    feedbackOptions: { successMode: "none", errorMode: "none" },
    getOptions: { responseType: "json", headers: requestHeaders, withCredentials: true },
    postOptions: { responseType: "json", headers: requestHeaders, withCredentials: true },
};

/**
 * Terminal-lifecycle service for the PHMS post-hearing surface. Drives the case-completion (story
 * 968) and closeout/freeze-package (story 972) endpoints through CrudApiService + BitsApis. The GET
 * reflectors treat 404 as "not yet completed/closed" (returns null) so the surface can render the
 * current terminal state without surfacing a spurious error on load.
 */
@Injectable({ providedIn: "root" })
export class PhmsCaseLifecycleService {
    private readonly bitsApis = inject(BitsApis);
    private readonly crudService = inject(CrudApiService);

    completeCase(
        workflowInstanceId: number,
        request: OperationsCaseCompletionRequest,
    ): Observable<OperationsCaseCompletionDto> {
        return this.crudService
            .postByUrl<OperationsCaseCompletionDto>(
                this.bitsApis.core.manual.postPhmsCaseComplete(workflowInstanceId),
                buildCompletionPayload(request),
                lifecycleApiOptions,
            )
            .pipe(map((response) => unwrapResponse(response)));
    }

    getCompletion(workflowInstanceId: number): Observable<OperationsCaseCompletionDto | null> {
        return this.crudService
            .getbyUrl<OperationsCaseCompletionDto>(
                this.bitsApis.core.manual.getPhmsCaseCompletion(workflowInstanceId),
                lifecycleApiOptions,
            )
            .pipe(
                map((response) => unwrapResponse(response) as OperationsCaseCompletionDto | null),
                catchError((error: unknown) => nullOnNotFound<OperationsCaseCompletionDto>(error)),
            );
    }

    closeoutCase(
        workflowInstanceId: number,
        request: OperationsCaseCloseoutRequest,
    ): Observable<OperationsCaseCloseoutDto> {
        return this.crudService
            .postByUrl<OperationsCaseCloseoutDto>(
                this.bitsApis.core.manual.postPhmsCaseCloseout(workflowInstanceId),
                buildCloseoutPayload(request),
                lifecycleApiOptions,
            )
            .pipe(map((response) => unwrapResponse(response)));
    }

    getCloseoutPackage(workflowInstanceId: number): Observable<OperationsCaseCloseoutDto | null> {
        return this.crudService
            .getbyUrl<OperationsCaseCloseoutDto>(
                this.bitsApis.core.manual.getPhmsCaseCloseoutPackage(workflowInstanceId),
                lifecycleApiOptions,
            )
            .pipe(
                map((response) => unwrapResponse(response) as OperationsCaseCloseoutDto | null),
                catchError((error: unknown) => nullOnNotFound<OperationsCaseCloseoutDto>(error)),
            );
    }
}

function buildCompletionPayload(
    request: OperationsCaseCompletionRequest,
): OperationsCaseCompletionRequest {
    const payload: OperationsCaseCompletionRequest = {};
    const reason = request.reason?.trim();
    if (reason) {
        payload.reason = reason;
    }
    if (typeof request.completedByParticipantId === "number") {
        payload.completedByParticipantId = request.completedByParticipantId;
    }
    return payload;
}

function buildCloseoutPayload(
    request: OperationsCaseCloseoutRequest,
): OperationsCaseCloseoutRequest {
    const payload: OperationsCaseCloseoutRequest = {};
    const packageReference = request.packageReference?.trim();
    if (packageReference) {
        payload.packageReference = packageReference;
    }
    if (typeof request.closedByParticipantId === "number") {
        payload.closedByParticipantId = request.closedByParticipantId;
    }
    return payload;
}

function nullOnNotFound<T>(error: unknown): Observable<T | null> {
    if (error instanceof HttpErrorResponse && error.status === 404) {
        return of(null);
    }
    return throwError(() => error);
}

function unwrapResponse<T>(response: GenResp<T> | T | undefined | null): T {
    if (response && typeof response === "object" && "data" in response) {
        return (response as GenResp<T>).data;
    }

    return response as T;
}
